import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useGetAllPublishedBlogsQuery, useRegenerateBacklinksMutation, AllPublishedBlog } from '../store/topicApi';
import Pagination from './Pagination';

const ITEMS_PER_PAGE = 9;

const CATEGORIES = ['All', 'QA', 'DevOps', 'AI/ML', 'Development', 'Security', 'Architecture', 'Power Platform', 'Business Analysis'];

const categoryColor = (cat?: string) => {
  const map: Record<string, string> = {
    QA: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    DevOps: 'bg-trident-orange-light text-trident-orange border-trident-orange/30',
    'AI/ML': 'bg-purple-100 text-purple-700 border-purple-200',
    Development: 'bg-blue-100 text-blue-700 border-blue-200',
    Security: 'bg-red-100 text-red-700 border-red-200',
    Architecture: 'bg-sky-100 text-sky-700 border-sky-200',
    'Power Platform': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'Business Analysis': 'bg-amber-100 text-amber-700 border-amber-200',
  };
  return map[cat || ''] || 'bg-trident-gray-100 text-trident-gray-700 border-trident-gray-200';
};

const BlogCard: React.FC<{ blog: AllPublishedBlog; onRegenerate: (id: string) => void; regenerating: boolean }> = ({ blog, onRegenerate, regenerating }) => (
  <div className="group bg-white rounded-2xl border border-trident-gray-100 hover:border-trident-orange/40 shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden flex flex-col">
    {blog.featuredImage && (
      <div className="aspect-video overflow-hidden bg-trident-gray-100">
        <img src={blog.featuredImage} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
    )}
    <div className="p-5 flex-1 flex flex-col">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {blog.category && (
          <span className={`inline-flex text-[10px] font-display font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider ${categoryColor(blog.category)}`}>
            {blog.category}
          </span>
        )}
        {blog.readTime && (
          <span className="text-[10px] text-trident-gray-500 font-display font-semibold">· {blog.readTime}</span>
        )}
        {blog.userEdited && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200">✏️ Edited</span>
        )}
      </div>
      <h3 className="font-display font-bold text-base text-trident-gray-900 leading-snug mb-2 line-clamp-2">
        {blog.title}
      </h3>
      {blog.excerpt && (
        <p className="text-sm text-trident-gray-500 line-clamp-2 mb-4 flex-1">{blog.excerpt}</p>
      )}
      <div className="flex items-center gap-2 pt-3 border-t border-trident-gray-100 text-xs text-trident-gray-500">
        <div className="w-6 h-6 rounded-full bg-trident-gradient flex items-center justify-center text-white font-display font-bold text-[10px]">
          {blog.authorName.split(' ').map(w => w[0]).slice(0, 2).join('')}
        </div>
        <span className="flex-1 truncate">{blog.authorName}</span>
        <span>{blog.publishedDate || '—'}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <a
          href={blog.publishedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-trident-gradient text-white font-display font-semibold text-xs shadow-trident hover:shadow-trident-lg hover:-translate-y-0.5 transition-all"
        >
          View Live
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
        </a>
        <button
          onClick={() => onRegenerate(blog._id)}
          disabled={regenerating}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-trident-gray-200 text-trident-gray-700 font-display font-semibold text-xs hover:border-trident-orange hover:text-trident-orange transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
          {blog.backlinkCount > 0 ? 'Regen Links' : 'Gen Links'}
        </button>
      </div>
    </div>
  </div>
);

const PublishedBlogs: React.FC = () => {
  const { data: blogs = [], isLoading, error } = useGetAllPublishedBlogsQuery();
  const [regenerateBacklinks, { isLoading: regenerating }] = useRegenerateBacklinksMutation();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedCat, setSelectedCat] = useState('All');
  const [processingId, setProcessingId] = useState<string | null>(null);

  const handleRegenerate = async (id: string) => {
    setProcessingId(id);
    try {
      await regenerateBacklinks(id).unwrap();
      toast.success('Backlinks regenerated successfully.');
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to regenerate backlinks');
    } finally {
      setProcessingId(null);
    }
  };

  const filtered = blogs.filter(b => {
    const matchCat = selectedCat === 'All' || b.category === selectedCat;
    const matchSearch = !search || b.title.toLowerCase().includes(search.toLowerCase()) || b.authorName.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-trident-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-10 text-red-500 font-semibold">Failed to load blogs.</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display font-bold text-2xl text-trident-gray-900">Published Blogs</h2>
        <p className="text-sm text-trident-gray-500">
          All {blogs.length} blogs currently live on the ITTrident website.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-trident-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            placeholder="Search by title or author..."
            className="input-field pl-10"
          />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-5">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => { setSelectedCat(cat); setCurrentPage(1); }}
            className={`px-3 py-1.5 rounded-lg font-display font-semibold text-xs transition-all ${
              selectedCat === cat
                ? 'bg-trident-gradient text-white shadow-trident'
                : 'bg-white border border-trident-gray-200 text-trident-gray-600 hover:border-trident-orange hover:text-trident-orange'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card text-center py-16">
          <h3 className="font-display font-bold text-lg text-trident-gray-900 mb-1">No blogs found</h3>
          <p className="text-sm text-trident-gray-500">Try adjusting your filters.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {paginated.map((b) => (
              <BlogCard key={b._id} blog={b} onRegenerate={handleRegenerate} regenerating={regenerating && processingId === b._id} />
            ))}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filtered.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </div>
  );
};

export default PublishedBlogs;
